import React, { useEffect, useMemo, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Users as UsersIcon, Search, Trash2, ShieldCheck, Truck, User as UserIcon, UserCog, LockKeyhole, LockKeyholeOpen, AlertTriangle, Smartphone, ChevronDown, ChevronUp, Monitor, Apple, Tablet, HelpCircle } from "lucide-react";
import { SortableHeader, useSort, makeComparator } from "@/components/SortableHeader";
import { toast } from "sonner";

/** Inline troubleshooter for the recurring "Email already used by another account"
 * blocker. Walks the admin through finding ALL records (user docs + customer-portal
 * links) that hold a given email, then offers a hard purge so they can re-create
 * the account under a different role. */
function EmailConflictTool() {
  const [email, setEmail] = useState("");
  const [matches, setMatches] = useState(null); // null = not searched yet
  const [busy, setBusy] = useState(false);

  const lookup = async () => {
    if (!email.trim()) return;
    setBusy(true); setMatches(null);
    try {
      const { data } = await api.get(`/admin/lookup-user-by-email?email=${encodeURIComponent(email.trim())}`);
      setMatches(data.matches);
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const purge = async () => {
    if (!window.confirm(`Permanently remove every record matching "${email}"? This cannot be undone.`)) return;
    setBusy(true);
    try {
      const { data } = await api.delete(`/admin/purge-user-by-email?email=${encodeURIComponent(email.trim())}`);
      toast.success(`Removed ${data.users_deleted} user record(s) and cleared ${data.customer_links_cleared} customer link(s). Email is now free to reuse.`);
      setMatches([]);
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="border border-amber-300 bg-amber-50 rounded-sm p-4 space-y-3" data-testid="email-conflict-tool">
      <div className="flex items-start gap-2">
        <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="font-semibold text-sm">&ldquo;Email already used&rdquo; troubleshooter</p>
          <p className="text-xs text-muted-foreground">Find every account hidden under an email — including soft-orphaned records that don&apos;t show up in the list above — and force-remove them so you can re-add the user under a new role.</p>
        </div>
      </div>
      <div className="flex gap-2 flex-wrap">
        <Input
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="email@example.com"
          className="rounded-sm flex-1 min-w-[200px]"
          data-testid="conflict-email-input"
        />
        <Button onClick={lookup} disabled={busy || !email.trim()} variant="outline" className="rounded-sm" data-testid="conflict-lookup">
          <Search className="w-4 h-4 mr-1" /> Find records
        </Button>
      </div>
      {matches !== null && (
        <div className="bg-background border border-border rounded-sm p-3 text-sm" data-testid="conflict-results">
          {matches.length === 0
            ? <p className="text-muted-foreground">No records found — this email is free to use.</p>
            : <>
                <p className="font-semibold mb-2">{matches.length} record(s) still holding this email:</p>
                <ul className="space-y-1 text-xs font-mono mb-3 max-h-40 overflow-auto">
                  {matches.map((m, i) => (
                    <li key={i} className="break-all">
                      {m.kind === "customer_portal_link"
                        ? `↳ customer portal link · ${m.name || m.customer_id} (enabled=${m.portal_enabled})`
                        : `↳ ${m.role || "user"} · ${m.name || "—"} · id=${m.id || "—"}`}
                    </li>
                  ))}
                </ul>
                <Button onClick={purge} disabled={busy} variant="destructive" className="rounded-sm" data-testid="conflict-purge">
                  <Trash2 className="w-4 h-4 mr-1" /> Force-remove all {matches.length} record(s)
                </Button>
              </>
          }
        </div>
      )}
    </div>
  );
}

const ROLE_META = {
  admin:      { Icon: ShieldCheck, label: "Admin",     cls: "text-primary" },
  secretary:  { Icon: UserCog,     label: "Secretary", cls: "text-blue-600" },
  driver:     { Icon: Truck,       label: "Driver",    cls: "text-amber-600" },
  customer:   { Icon: UserIcon,    label: "Customer",  cls: "text-muted-foreground" },
};

const fmtDeviceDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
  catch (_) { return iso; }
};

// Map the backend's device_type string to an icon + visual color, so the
// admin can tell at a glance whether a trusted device is a phone, tablet, or
// desktop.
const DEVICE_ICON = {
  "iPhone":     { Icon: Apple,      cls: "text-foreground" },
  "iPad":       { Icon: Tablet,     cls: "text-foreground" },
  "Mac":        { Icon: Apple,      cls: "text-foreground" },
  "Android":    { Icon: Smartphone, cls: "text-success" },
  "Windows PC": { Icon: Monitor,    cls: "text-primary" },
  "Linux":      { Icon: Monitor,    cls: "text-primary" },
  "Other":      { Icon: HelpCircle, cls: "text-muted-foreground" },
  "Unknown":    { Icon: HelpCircle, cls: "text-muted-foreground" },
};

/** Inline list of trusted (email-verified) devices for a single user, with
 * per-device delete + "Sign out all devices" actions. Mounted on demand so
 * we don't fan-out a request for every row when the admin opens the page. */
function TrustedDevicesPanel({ uid, email }) {
  const [devices, setDevices] = useState(null); // null = loading
  const [busy, setBusy] = useState(false);

  const load = React.useCallback(() => {
    setDevices(null);
    api.get(`/users/${uid}/trusted-devices`)
      .then((r) => setDevices(r.data))
      .catch((e) => { toast.error(formatErr(e.response?.data?.detail) || e.message); setDevices([]); });
  }, [uid]);
  useEffect(() => { load(); }, [load]);

  const revokeOne = async (token) => {
    if (!window.confirm("Forget this device? They'll need a fresh email code next time they sign in from it.")) return;
    setBusy(true);
    try {
      await api.delete(`/users/${uid}/trusted-devices/${encodeURIComponent(token)}`);
      toast.success("Device removed");
      load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const revokeAll = async () => {
    if (!window.confirm(`Forget ALL devices for ${email}? They'll get an email code on the next sign-in from any browser or phone.`)) return;
    setBusy(true);
    try {
      await api.delete(`/users/${uid}/trusted-devices`);
      toast.success("All devices removed");
      load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-secondary/30 border-t border-border px-6 py-4" data-testid={`user-devices-panel-${uid}`}>
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
          <Smartphone className="w-3.5 h-3.5" /> Recognized devices
          {Array.isArray(devices) && <span className="font-mono text-foreground">({devices.length})</span>}
        </div>
        {Array.isArray(devices) && devices.length > 0 && (
          <Button size="sm" variant="outline" disabled={busy} onClick={revokeAll} className="rounded-sm text-destructive hover:text-destructive h-7" data-testid={`user-devices-revoke-all-${uid}`}>
            <Trash2 className="w-3.5 h-3.5 mr-1" /> Sign out all
          </Button>
        )}
      </div>
      {devices === null ? (
        <p className="text-xs text-muted-foreground font-mono">Loading…</p>
      ) : devices.length === 0 ? (
        <p className="text-xs text-muted-foreground">No trusted devices — this account will require an email code on every new sign-in.</p>
      ) : (
        <div className="space-y-1">
          {devices.map((d) => {
            const di = DEVICE_ICON[d.device_type] || DEVICE_ICON.Unknown;
            const DIcon = di.Icon;
            const sameIp = d.created_ip && d.last_used_ip && d.created_ip === d.last_used_ip;
            return (
            <div key={d.token} className="flex items-center justify-between gap-3 bg-background border border-border rounded-sm px-3 py-2" data-testid={`user-device-${uid}-${d.fingerprint}`}>
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <DIcon className={`w-5 h-5 shrink-0 ${di.cls}`} />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold leading-tight" data-testid={`user-device-label-${uid}-${d.fingerprint}`}>
                    {d.label || d.device_type || "Unknown device"}
                  </div>
                  <div className="text-[10px] text-muted-foreground font-mono leading-relaxed">
                    <div>
                      <span className="opacity-60">First trusted</span> {fmtDeviceDate(d.created_at)}
                      {d.created_ip && <span> · IP <span className="text-foreground/80">{d.created_ip}</span></span>}
                    </div>
                    <div>
                      <span className="opacity-60">Last used</span> {fmtDeviceDate(d.last_used_at)}
                      {d.last_used_ip && (
                        <span> · IP <span className={`text-foreground/80 ${!sameIp && d.created_ip ? "text-primary font-semibold" : ""}`}>{d.last_used_ip}</span>
                          {!sameIp && d.created_ip && <span className="text-primary"> (different network)</span>}
                        </span>
                      )}
                    </div>
                    <div className="opacity-50">···{d.fingerprint}</div>
                  </div>
                </div>
              </div>
              <Button size="sm" variant="ghost" disabled={busy} onClick={() => revokeOne(d.token)} className="rounded-sm text-destructive hover:text-destructive h-7" data-testid={`user-device-delete-${uid}-${d.fingerprint}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          );})}
        </div>
      )}
    </div>
  );
}

export default function Users() {
  const [list, setList] = useState([]);
  const [tab, setTab] = useState("all");
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(() => new Set());
  const { sortKey, sortDir, toggleSort } = useSort("role", "asc");

  const toggleExpand = (uid) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid); else next.add(uid);
      return next;
    });
  };

  const load = () => api.get("/users").then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const counts = useMemo(() => list.reduce((acc, u) => {
    acc[u.role] = (acc[u.role] || 0) + 1; return acc;
  }, {}), [list]);

  const filtered = useMemo(() => {
    let arr = tab === "all" ? list : list.filter((u) => u.role === tab);
    if (search.trim()) {
      const q = search.toLowerCase();
      arr = arr.filter((u) =>
        (u.email || "").toLowerCase().includes(q) ||
        (u.name || "").toLowerCase().includes(q) ||
        (u.business_name || "").toLowerCase().includes(q)
      );
    }
    arr = [...arr].sort(makeComparator((u) => {
      switch (sortKey) {
        case "role":   return (u.role || "").toLowerCase();
        case "name":   return (u.business_name || u.name || u.email || "").toLowerCase();
        case "email":  return (u.email || "").toLowerCase();
        case "status": return u.locked ? 2 : (u.disabled ? 1 : 0);
        case "created":return u.created_at || "";
        default: return "";
      }
    }, sortDir));
    return arr;
  }, [list, tab, search, sortKey, sortDir]);

  const toggle = async (u) => {
    if (u.is_self) return toast.error("You can't disable your own account");
    if (u.role === "admin") return toast.error("Admin accounts can't be disabled here");
    try {
      await api.patch(`/users/${u.id}/status`, { disabled: !u.disabled });
      toast.success(!u.disabled ? "Account disabled" : "Account enabled");
      load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || e.message); }
  };

  const unlock = async (u) => {
    try {
      await api.post(`/users/${u.id}/unlock`);
      toast.success(`${u.email} unlocked — they can sign in with their password again`);
      load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || e.message); }
  };

  const remove = async (u) => {
    if (u.is_self) return toast.error("You can't delete your own account");
    if (u.role === "admin") return toast.error("Admin accounts can't be deleted here");
    if (!window.confirm(`Permanently delete ${u.name || u.email}? They will lose access immediately.`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("User deleted");
      load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || e.message); }
  };

  return (
    <div className="space-y-6 animate-fade-up">
      <div>
        <p className="overline">Access control</p>
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter mt-1">Users</h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl flex items-center gap-2"><UsersIcon className="w-4 h-4" /> All staff and customer-portal accounts. Disable an account to revoke login without losing their history. Delete only removes the login — customer records and invoices stay intact.</p>
      </div>

      {/* Email-conflict troubleshooter — visible above the list so it's
          discoverable when the admin hits "email already used" elsewhere. */}
      <EmailConflictTool />

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <Tabs value={tab} onValueChange={setTab} className="overflow-x-auto -mx-1 px-1 sm:overflow-visible">
          <TabsList className="rounded-sm inline-flex w-auto">
            <TabsTrigger value="all" data-testid="users-tab-all">All ({list.length})</TabsTrigger>
            <TabsTrigger value="admin" data-testid="users-tab-admin">Admin ({counts.admin || 0})</TabsTrigger>
            <TabsTrigger value="secretary" data-testid="users-tab-secretary">Secretary ({counts.secretary || 0})</TabsTrigger>
            <TabsTrigger value="driver" data-testid="users-tab-driver">Driver ({counts.driver || 0})</TabsTrigger>
            <TabsTrigger value="customer" data-testid="users-tab-customer">Customer ({counts.customer || 0})</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="relative w-full sm:max-w-sm">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          <Input className="rounded-sm pl-9" placeholder="Search name or email…" value={search}
                 onChange={(e) => setSearch(e.target.value)} data-testid="users-search" />
        </div>
      </div>

      <div className="border border-border overflow-x-auto">
        <div className="min-w-[760px]">
          <div className="grid grid-cols-12 px-6 py-3 border-b border-border overline items-center">
            <SortableHeader sortKey={sortKey} sortDir={sortDir} columnKey="role" onToggle={toggleSort} className="col-span-2" testId="sort-role">Role</SortableHeader>
            <SortableHeader sortKey={sortKey} sortDir={sortDir} columnKey="name" onToggle={toggleSort} className="col-span-3" testId="sort-name">Name</SortableHeader>
            <SortableHeader sortKey={sortKey} sortDir={sortDir} columnKey="email" onToggle={toggleSort} className="col-span-4" testId="sort-email">Email</SortableHeader>
            <SortableHeader sortKey={sortKey} sortDir={sortDir} columnKey="status" onToggle={toggleSort} className="col-span-1" testId="sort-status">Status</SortableHeader>
            <div className="col-span-2 text-right">Actions</div>
          </div>
          {filtered.length === 0 && (
            <div className="px-6 py-10 text-center text-sm text-muted-foreground">No users match.</div>
          )}
          {filtered.map((u) => {
            const meta = ROLE_META[u.role] || ROLE_META.customer;
            const Icon = meta.Icon;
            const locked = u.is_self || u.role === "admin";
            const isOpen = expanded.has(u.id);
            return (
              <React.Fragment key={u.id}>
              <div className="grid grid-cols-12 px-6 py-3 border-b border-border last:border-b-0 hover:bg-secondary/40 items-center" data-testid={`user-row-${u.id}`}>
                <div className="col-span-2 flex items-center gap-2">
                  <Icon className={`w-4 h-4 shrink-0 ${meta.cls}`} />
                  <span className="text-xs font-bold uppercase tracking-wider">{meta.label}</span>
                </div>
                <div className="col-span-3 text-sm">
                  <div className="font-medium truncate">{u.business_name || u.name || "—"}</div>
                  {u.business_name && u.name && u.name !== u.business_name && (
                    <div className="text-xs text-muted-foreground truncate">{u.name}</div>
                  )}
                </div>
                <div className="col-span-4 text-sm font-mono text-muted-foreground truncate">{u.email}</div>
                <div className="col-span-1">
                  {u.locked ? (
                    <span className="text-xs font-bold uppercase tracking-wider text-destructive inline-flex items-center gap-1" data-testid={`user-locked-badge-${u.id}`}>
                      <LockKeyhole className="w-3 h-3" /> Locked
                    </span>
                  ) : u.disabled ? (
                    <span className="text-xs font-bold uppercase tracking-wider text-destructive">Off</span>
                  ) : (
                    <span className="text-xs font-bold uppercase tracking-wider text-success">On</span>
                  )}
                </div>
                <div className="col-span-2 flex justify-end items-center gap-2">
                  <Button
                    size="sm" variant="ghost"
                    onClick={() => toggleExpand(u.id)}
                    className="rounded-sm text-muted-foreground hover:text-foreground"
                    title="Recognized devices"
                    data-testid={`user-devices-toggle-${u.id}`}
                  >
                    <Smartphone className="w-3.5 h-3.5 mr-1" />
                    {isOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                  </Button>
                  {u.locked && (
                    <Button
                      size="sm" variant="outline"
                      onClick={() => unlock(u)}
                      className="rounded-sm text-primary hover:text-primary"
                      title="Re-enable the password and reset failed sign-in attempts"
                      data-testid={`user-unlock-${u.id}`}
                    >
                      <LockKeyholeOpen className="w-3.5 h-3.5 mr-1" /> Unlock
                    </Button>
                  )}
                  <Switch
                    checked={!u.disabled}
                    disabled={locked}
                    onCheckedChange={() => toggle(u)}
                    data-testid={`user-toggle-${u.id}`}
                  />
                  <Button
                    size="sm" variant="ghost"
                    disabled={locked}
                    onClick={() => remove(u)}
                    className="rounded-sm text-destructive hover:text-destructive"
                    data-testid={`user-delete-${u.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
              {isOpen && (
                <TrustedDevicesPanel uid={u.id} email={u.email} />
              )}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
}
